"use client";

import { useState } from "react";
import { useRouter } from "next/navigation";

export function MergeDraft({
  sessionToken,
  title,
}: {
  sessionToken: string;
  title: string | null;
}) {
  const router = useRouter();
  const [claiming, setClaiming] = useState(false);
  const [finalisingPccp, setFinalisingPccp] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function claim() {
    setClaiming(true);
    setFinalisingPccp(false);
    setError(null);
    try {
      for (let attempt = 0; attempt < 90; attempt += 1) {
        const res = await fetch(`/api/draft-projects/${sessionToken}/claim`, {
          method: "POST",
        });
        const data = (await res.json()) as { redirect?: string; error?: string };
        if (res.ok && data.redirect) {
          router.push(data.redirect);
          return;
        }
        if (data.error === "draft_pcpp_processing" && attempt < 89) {
          setFinalisingPccp(true);
          await new Promise((resolve) => window.setTimeout(resolve, 2500));
          continue;
        }
        throw new Error(data.error ?? "Could not claim this draft");
      }
      throw new Error("The PCCP is still being checked. Please try again.");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Something went wrong");
      setClaiming(false);
      setFinalisingPccp(false);
    }
  }

  return (
    <div className="rounded-lg border border-border bg-surface p-6">
      <h1 className="font-heading text-2xl font-extrabold text-navy">Save this draft to your account</h1>
      <p className="mt-3 text-sm leading-6 text-muted">
        {finalisingPccp
          ? "The original PCCP is saved. We are finishing the PDF check before opening your project."
          : `You are signed in and have an unfinished draft${title ? ` for ${title}` : ""}. Claim it now to keep working in your project workspace.`}
      </p>
      {error ? <p className="mt-4 text-sm font-semibold text-danger">{error}</p> : null}
      <button
        type="button"
        onClick={claim}
        disabled={claiming}
        className="mt-6 inline-flex min-h-[44px] items-center justify-center rounded-lg bg-accent px-5 py-3 text-sm font-bold text-white hover:bg-accent-dark disabled:opacity-50"
      >
        {finalisingPccp ? "Finalising PCCP..." : claiming ? "Claiming..." : "Claim and continue"}
      </button>
    </div>
  );
}

